/* eslint-disable react-refresh/only-export-components */
import {
  type CalendarAccountsStatusResponse,
  getMeetingNoteSource,
  type Meeting as StoreMeeting,
  useStores,
  useUserConfig,
} from '@littlebird/sdk';
import {
  circleMemberEmails,
  collectUpcomingMeetingIds,
  getMeetingGroupLabel,
  groupMeetingsByDate,
  isInCircle,
  type MeetingCircle,
  usePagedLoad,
  type MeetingDateGroup,
} from '@littlebird/ui-utils';
import { useNavigate } from '@tanstack/react-router';
import { isToday } from 'date-fns/isToday';
import { observer } from 'mobx-react-lite';
import { AnimatePresence, motion } from 'motion/react';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { type VirtualizerHandle, VList } from 'virtua';

import { Icons } from '@/assets/icons/icons';
import { ModalsManager } from '@/components/modals/modalsManager';
import {
  Button,
  IconButton,
  Text,
  Time,
  XStack,
  YStack,
} from '@/components/ui';
import { Avatar } from '@/components/ui/Avatar';
import { Checkbox } from '@/components/ui/Checkbox';
import { DropdownMenu } from '@/components/ui/dropdown-menu';
import { Empty } from '@/components/ui/EmptyState';
import { EntityIcon } from '@/components/ui/icon-picker';
import { ActiveDot } from '@/components/ui/layout/active-dot';
import { Page } from '@/design-system-v2/page';
import { RoundButton } from '@/design-system-v2/round-button';
import { RoundSearchInput } from '@/design-system-v2/round-search-input';
import {
  ContextChatDock,
  useContextChatTarget,
  type ChatTarget,
} from '@/features/context-chat';
import { electronApis } from '@/features/electron-api';
import { MeetingDebugMenuItem } from '@/features/meeting-debug';
import { ShareMeetingModal } from '@/features/meetings/meeting-share.modals';
import {
  BulkMoveToProjectDropdown,
  MoveToProjectDropdownButton,
  MoveToProjectDropdownSubMenu,
} from '@/features/projects/move-to-project';
import { getProjectSidebarIcon } from '@/features/projects/project-sidebar-icon';
import { openSettings } from '@/features/settings/settings-navigation';
import { MeetingLimitBanner, MeetingUsageBadge } from '@/features/subscription';
import { isNewTabClick, useTabs } from '@/features/tabs';
import { MeetingsDownloadCallout } from '@/features/web-download';
import { useEndReachedLoadMore } from '@/hooks/use-end-reached-load-more';
import { useScrollFadeForOffset } from '@/hooks/useScrollFade';
import { useVListScrollPersistence } from '@/hooks/useVListScrollPersistence';
import { pluralize } from '@/lib/date-utils';
import { logError } from '@/lib/errorLogging';
import { cn } from '@/lib/utils';
import { Route } from '@/routes/_authenticated/meetings/index';
import { userStore } from '@/stores/user.store';
import { trackEvent } from '@/utils/analytics';
import {
  AppCapability,
  inBrowser,
  inElectron,
  isCapable,
} from '@/utils/capabilities';
import { getLocalStorageItem, setLocalStorageItem } from '@/utils/localStorage';

import { AutomaticMeetingPrepBanner } from './automatic-meeting-prep-banner';
import { CalendarSyncControl } from './calendar-sync-control';
import {
  getDefaultMeetingTab,
  getDisplayAttendees,
  getOtherAttendees,
  storeMeetingToMeeting,
} from './meeting-mapper';
import { MeetingNoteSourceBadge } from './meeting-note-source-badge';
import { MeetingOptInBanner } from './meeting-opt-in-banner';
import { useEnsureProjectLoaded } from './meeting-project-chip';
import { ShareMeetingPopover } from './meeting-share.modals';
import {
  AttendeeAvatar,
  CircleFaces,
  UpcomingEventCard,
  UpcomingEventCardSkeleton,
} from './meetings-common.components';
import {
  MeetingsEmptyState,
  MeetingsNoUpcomingEventsEmptyState,
  MeetingsProjectEmptyState,
  MeetingsSearchEmptyState,
} from './meetings-empty-state';
import { type MeetingsFilter } from './meetings-filter';
import { useMeetingListPagination } from './meetings-list-pagination';
import {
  filterMeetingIdsBySearch,
  formatAttendees,
} from './meetings-list.utils';
import { OnboardingChecklist } from './meetings-onboarding-checklist';
import {
  meetingsSidebarStore,
  NO_PROJECT_SLICE,
} from './meetings-sidebar.store';
import {
  isNewNoteDisabled,
  startMeetingFromOverview,
  useCircleMeetingsLoad,
  useSelectedCircle,
  useUpcomingMeetings,
} from './meetings.hooks';
import { meetingApis } from './meetings.query';
import { meetingStore } from './meetings.state';
import {
  type CalendarEvent,
  type CalendarEventAttendee,
  getMeetingTitle,
  type Meeting,
  type MeetingId,
} from './meetings.types';
import { ProjectSliceSharedMeetings } from './project-slice-shared-meetings';

export const MeetingNoteDropdownItems = observer(
  ({ meetingId, onShare }: { meetingId: string; onShare?: () => void }) => {
    const isRecording = meetingStore.isMeetingIdRecording(meetingId);
    const isPrepState = meetingStore.isPrepState(meetingId);

    return (
      <>
        <MoveToProjectDropdownSubMenu meetingId={meetingId} />
        {!userStore.hasSignedBaa && (
          <DropdownMenu.Item
            className="items-center"
            disabled={isPrepState}
            onClick={(e) => {
              e.stopPropagation();
              if (onShare) {
                onShare();
                return;
              }
              ModalsManager.show(ShareMeetingModal, {
                meetingId,
              });
            }}
          >
            <Icons.share />
            Share
          </DropdownMenu.Item>
        )}
        <MeetingDebugMenuItem meetingId={meetingId} />
        <DropdownMenu.ItemWithConfirm
          className="items-center"
          onConfirm={() => meetingApis.deleteMeetingNotes([meetingId])}
          icon={Icons.trash}
          ariaLabel="Delete meeting note"
          disabled={isRecording}
        >
          Delete
        </DropdownMenu.ItemWithConfirm>
      </>
    );
  }
);

const MEETING_TITLE_SHIMMER_CLASSES = [
  'bg-[linear-gradient(to_right,var(--gray-11)_0%,var(--gray-11)_67.5%,var(--gray-12)_72.5%,var(--gray-12)_87.5%,var(--gray-11)_92.5%,var(--gray-11)_100%)]',
  'bg-[length:200%_100%]',
  'bg-clip-text text-transparent',
  '[background-position:0%_0]',
  'animate-[shimmerRenameField_5s_linear_infinite]',
];

const AUTOMATIC_RECORDING_PROMPT_DISMISSED_KEY_PREFIX =
  'meeting-auto-record-prompt-dismissed-v1';

const CHAT_DOCK_INSET_VAR = '--chat-dock-inset';
const CHAT_DOCK_INSET = `var(${CHAT_DOCK_INSET_VAR}, 0px)`;

const GENERAL_CHAT_TARGET: ChatTarget = { type: 'general' };

export const MeetingsListView = memo(function MeetingsListView() {
  const [query, setQuery] = useState('');
  const { meetings } = useStores();

  useEffect(() => {
    meetings.ensureListLoadedFromStorage().catch((err) => {
      logError(err, 'Failed to hydrate meetings from local storage', {
        context: 'MeetingsListView:ensureListLoadedFromStorage',
      });
    });
  }, [meetings]);

  useContextChatTarget(GENERAL_CHAT_TARGET, true);

  return (
    <div data-chat-dock-scope className="h-full relative flex flex-col">
      <div className={cn(Page.column(), 'shrink-0')}>
        <YStack className="gap-4">
          <Header
            toolbar={<MeetingsToolbar query={query} onChange={setQuery} />}
          />
          <AutomaticMeetingRecordingPrompt />
        </YStack>
      </div>
      <MeetingNotesSection query={query} />
      <ContextChatDock maxWidthClassName={Page.readingWidth} />
    </div>
  );
});

const AutomaticMeetingRecordingPrompt = observer(
  function AutomaticMeetingRecordingPrompt() {
    const userConfig = useUserConfig();
    const userId = userStore.user?.id;
    const dismissalKey = userId
      ? `${AUTOMATIC_RECORDING_PROMPT_DISMISSED_KEY_PREFIX}:${userId}`
      : null;
    const [dismissed, setDismissed] = useState(
      () => dismissalKey !== null && getLocalStorageItem(dismissalKey) === true
    );
    const autoStart = userConfig.config.meetings.auto_start_on_detection;
    const meetingDetectionMode = userConfig.config.meetings
      .disable_detection_notification
      ? 'do_nothing'
      : 'notify_me';
    const visible =
      dismissalKey !== null &&
      isCapable(AppCapability.RecordMeeting) &&
      !autoStart &&
      !dismissed;

    useEffect(() => {
      if (visible) {
        trackEvent('MEETING_AUTO_RECORDING_BANNER_VIEWED', {
          meeting_detection_mode: meetingDetectionMode,
        });
      }
    }, [meetingDetectionMode, visible]);

    if (!visible || dismissalKey === null) return null;

    const turnOn = () => {
      trackEvent('MEETING_AUTO_RECORDING_BANNER_CTA_CLICKED', {
        meeting_detection_mode: meetingDetectionMode,
      });
      userConfig.meetings.patch({
        disable_detection_notification: false,
        auto_start_on_detection: true,
      });
      electronApis.meetings?.logAutoStartEvent?.('settings_changed', {
        key: 'meeting_auto_start_on_detection',
        previous_value: autoStart,
        new_value: true,
        trigger: 'user_toggle',
      });
    };

    const dismiss = () => {
      trackEvent('MEETING_AUTO_RECORDING_BANNER_DISMISSED', {
        meeting_detection_mode: meetingDetectionMode,
      });
      setLocalStorageItem(dismissalKey, true);
      setDismissed(true);
    };

    return (
      <div role="region" aria-label="Automatic meeting recording">
        <MeetingOptInBanner
          title="Record meetings automatically"
          subtitle="Littlebird will detect meetings and start recording for you."
          turnOnAriaLabel="Turn on automatic meeting recording"
          dismissAriaLabel="Not now, automatic meeting recording"
          onTurnOn={turnOn}
          onDismiss={dismiss}
        />
      </div>
    );
  }
);

const circleSubtitle = (circle: MeetingCircle) => {
  const count = circle.meetingIds.length;
  const meetings = `${count} ${pluralize(count, 'meeting')}`;
  if (circle.seriesId) return `Recurring · ${meetings}`;
  if (circle.emails.length === 1) return `1:1 · ${meetings}`;
  return `${circle.emails.length} people · ${meetings}`;
};

const SliceTitleLayout = ({
  leading,
  title,
  subtitle,
}: {
  leading: React.ReactNode;
  title: string;
  subtitle: string;
}) => (
  <XStack className="items-center gap-3 pb-6">
    {leading}
    <YStack className="min-w-0 flex-1">
      <span className="truncate font-serif text-xl leading-7 first-letter:uppercase">
        {title}
      </span>
      <span className="text-sm text-foreground-muted">{subtitle}</span>
    </YStack>
  </XStack>
);

const SliceTileIcon = ({ children }: { children: React.ReactNode }) => (
  <span className="grid size-10 shrink-0 place-items-center rounded-xl bg-surface-hover">
    {children}
  </span>
);

const ProjectSliceTitle = observer(function ProjectSliceTitle({
  projectId,
}: {
  projectId: string;
}) {
  'use no memo';
  const { projects } = useStores();
  useEnsureProjectLoaded(projectId);
  const project = projects.getById(projectId);

  return (
    <SliceTitleLayout
      leading={
        <SliceTileIcon>
          {project ? (
            <EntityIcon
              icon={getProjectSidebarIcon(project, false)}
              size={20}
            />
          ) : (
            <Icons.folder className="size-5 text-foreground-muted" />
          )}
        </SliceTileIcon>
      }
      title={project?.name ?? 'Project'}
      subtitle="Project"
    />
  );
});

const SliceTitle = observer(function SliceTitle() {
  'use no memo';
  const circle = useSelectedCircle();
  const { projectSlice } = meetingsSidebarStore;

  if (projectSlice === NO_PROJECT_SLICE) {
    return (
      <SliceTitleLayout
        leading={
          <SliceTileIcon>
            <Icons.circleDashed className="size-5 text-foreground-muted" />
          </SliceTileIcon>
        }
        title="No project"
        subtitle="Meetings not in a project"
      />
    );
  }
  if (projectSlice) return <ProjectSliceTitle projectId={projectSlice} />;
  if (!circle) return null;

  return (
    <SliceTitleLayout
      leading={<CircleFaces circle={circle} size="lg" />}
      title={circle.name}
      subtitle={circleSubtitle(circle)}
    />
  );
});

const NewNoteButton = observer(function NewNoteButton() {
  'use no memo';
  if (!isCapable(AppCapability.RecordMeeting)) return null;

  return (
    <RoundButton
      id="new-meeting-note-button"
      icon={meetingStore.isStartingMeeting ? Icons.loader : Icons.mic}
      disabled={isNewNoteDisabled()}
      onClick={() => startMeetingFromOverview()}
    >
      New note
    </RoundButton>
  );
});

const MeetingsToolbar = observer(function MeetingsToolbar({
  query,
  onChange,
}: {
  query: string;
  onChange: (value: string) => void;
}) {
  const { meetings } = useStores();
  if (meetings.isLoaded && meetings.sortedMeetings.length === 0) return null;
  return (
    <RoundSearchInput
      value={query}
      onChange={onChange}
      placeholder="Search meetings..."
      aria-label="Search meetings"
      className="w-60"
    />
  );
});

const Header = observer(function Header({
  toolbar,
}: {
  toolbar?: React.ReactNode;
}) {
  const navigate = useNavigate();
  const { meetings } = useStores();
  const isLoading = !meetings.isLoaded;
  const search = Route.useSearch();

  const hasAutoStartedRef = useRef(false);

  // Auto-start from deep link when ?start=true and data is ready (Electron only)
  useEffect(() => {
    if (!inElectron()) return;
    if (!search.start || isLoading || hasAutoStartedRef.current) return;

    hasAutoStartedRef.current = true;

    // Clear the search param immediately
    navigate({ to: '/meetings', search: {}, replace: true });

    startMeetingFromOverview();
  }, [search.start, isLoading, navigate]);

  useEffect(() => {
    if (!search.settings) return;
    void navigate({ to: '/meetings', search: {}, replace: true }).then(() =>
      openSettings('Meetings')
    );
  }, [search.settings, navigate]);

  return (
    <Page.HeaderActions>
      <XStack className="items-center gap-3">
        {toolbar}
        <MeetingUsageBadge />
        <NewNoteButton />
      </XStack>
    </Page.HeaderActions>
  );
});

interface MicPermissionBannerVisibilityInput {
  isElectron: boolean;
  isMeetingsLoaded: boolean;
  hasMicrophonePermission: boolean;
  shouldShowOnboardingChecklist: boolean;
}

export function shouldShowMicPermissionBanner({
  isElectron,
  isMeetingsLoaded,
  hasMicrophonePermission,
  shouldShowOnboardingChecklist,
}: MicPermissionBannerVisibilityInput): boolean {
  return (
    isElectron &&
    isMeetingsLoaded &&
    !hasMicrophonePermission &&
    !shouldShowOnboardingChecklist
  );
}

const MicPermissionBanner = observer(function MicPermissionBanner() {
  const [isRequestingPermission, setIsRequestingPermission] = useState(false);
  const { meetings } = useStores();

  const shouldShowBanner = shouldShowMicPermissionBanner({
    isElectron: inElectron(),
    isMeetingsLoaded: meetings.isLoaded,
    hasMicrophonePermission: meetingStore.hasMicrophonePermission,
    shouldShowOnboardingChecklist: meetingStore.shouldShowOnboardingChecklist,
  });

  if (!shouldShowBanner) return null;

  const handleGrantPermission = async () => {
    if (isRequestingPermission) return;

    setIsRequestingPermission(true);
    try {
      // This re-prompts when status is not determined
      // If mic was already denied, this opens System Settings > Privacy & Security > Microphone
      await meetingApis.requestMacPermissions(['microphone']);
    } finally {
      setIsRequestingPermission(false);
    }
  };

  return (
    <Empty.Root variant="action">
      <Empty.Header>
        <Empty.Title>Permission required</Empty.Title>
        <Empty.Description>
          Grant microphone access to enable recording. If you already denied it,
          enable Littlebird in System Settings &gt; Privacy &amp; Security &gt;
          Microphone.
        </Empty.Description>
      </Empty.Header>
      <Empty.Actions>
        <Button
          onClick={handleGrantPermission}
          isLoading={isRequestingPermission}
          disabled={isRequestingPermission}
        >
          Grant permission
        </Button>
      </Empty.Actions>
    </Empty.Root>
  );
});

const CalendarStatusBanner = observer(function CalendarStatusBanner() {
  const { meetings } = useStores();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [accountsStatus, setAccountsStatus] =
    useState<CalendarAccountsStatusResponse | null>(null);

  useEffect(() => {
    const fetchStatus = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const result = await meetings.refreshCalendarAccountsStatus();
        setAccountsStatus(result);
      } catch (err: unknown) {
        setError(
          err instanceof Error
            ? err.message
            : 'Failed to check calendar connections'
        );
      } finally {
        setIsLoading(false);
      }
    };

    if (meetings.isLoaded) {
      fetchStatus();
    }
  }, [meetings.isLoaded, meetings]);

  if (meetingStore.shouldShowOnboardingChecklist) return null;

  if (isLoading || error || !accountsStatus) return null;

  if (accountsStatus.accounts.length === 0) return null;

  const needsReconnection = accountsStatus.accounts.some(
    (account) =>
      account.connection_status === 'expired' ||
      account.connection_status === 'failed'
  );

  if (!needsReconnection) return null;

  const affectedAccounts = accountsStatus.accounts.filter(
    (account) =>
      account.connection_status === 'expired' ||
      account.connection_status === 'failed'
  );
  const accountLabels = affectedAccounts.map(
    (account) =>
      account.account_label || account.display_name || account.toolkit_name
  );

  const title = affectedAccounts.some(
    (account) => account.connection_status === 'expired'
  )
    ? 'Calendar Connection Expired'
    : 'Calendar Connection Failed';

  const description =
    accountLabels.length === 1
      ? `Your ${accountLabels[0]} calendar connection needs to be reconnected.`
      : `The following calendar connections need attention: ${accountLabels.join(', ')}.`;

  const handleReconnect = () => openSettings('Integrations');

  return (
    <Empty.Root variant="action">
      <Empty.Header>
        <Empty.Title>{title}</Empty.Title>
        <Empty.Description>{description}</Empty.Description>
      </Empty.Header>
      <Empty.Actions>
        <Button onClick={handleReconnect}>Reconnect in Integrations</Button>
      </Empty.Actions>
    </Empty.Root>
  );
});

function getAttendeeLabel(attendee: CalendarEventAttendee): string {
  return attendee.displayName || attendee.email || 'Attendee';
}

function MeetingItemFallbackIcon() {
  return (
    <XStack
      data-testid="meeting-item-fallback-icon"
      className="items-center justify-center size-full"
    >
      <Icons.quote className="size-5" />
    </XStack>
  );
}

function MeetingItemLeftVisual({
  attendees,
}: {
  attendees: CalendarEventAttendee[] | null | undefined;
}) {
  const list = getOtherAttendees(attendees, userStore.user?.email);
  const showDiagonal = list.length >= 2;
  const singleAttendee = list.length === 1 ? list[0] : undefined;
  const topAttendee = list.find((attendee) => attendee.avatarUrl) ?? list[0];
  const secondAttendee =
    list.find((attendee) => attendee !== topAttendee) ?? list[1];
  const ringClass = 'ring-2 ring-surface';

  return (
    <div className="relative size-12 rounded-xl border-[0.5px] border-border-strong bg-surface text-foreground-muted shrink-0">
      {showDiagonal ? (
        <>
          <AttendeeAvatar
            name={topAttendee.displayName}
            email={topAttendee.email}
            avatarUrl={topAttendee.avatarUrl}
            className={cn('absolute top-1.5 left-1.5 size-[21px]', ringClass)}
          />
          {list.length === 2 ? (
            <AttendeeAvatar
              name={secondAttendee.displayName}
              email={secondAttendee.email}
              avatarUrl={secondAttendee.avatarUrl}
              className={cn(
                'absolute bottom-1.5 right-1.5 size-[21px]',
                ringClass
              )}
            />
          ) : (
            <div
              className={cn(
                'absolute bottom-1.5 right-1.5 size-[21px] rounded-full',
                'bg-secondary-active flex items-center justify-center',
                ringClass
              )}
            >
              <span className="text-foreground-muted text-[10px] font-bold">
                +{list.length - 1}
              </span>
            </div>
          )}
        </>
      ) : singleAttendee ? (
        <Avatar
          avatarUrl={singleAttendee.avatarUrl}
          alt={getAttendeeLabel(singleAttendee)}
          imageClassName="size-full rounded-xl object-cover"
          fallback={
            <span className="flex size-full items-center justify-center text-foreground-muted text-lg font-bold">
              {getAttendeeLabel(singleAttendee).charAt(0).toUpperCase()}
            </span>
          }
        />
      ) : (
        <MeetingItemFallbackIcon />
      )}
    </div>
  );
}

interface MeetingNoteCardProps {
  meetingId: MeetingId;
  isSelected: boolean;
  selectionActive: boolean;
  onToggleSelect: (id: MeetingId) => void;
}

const MeetingNoteCard = observer(function MeetingNoteCard({
  meetingId,
  isSelected,
  selectionActive,
  onToggleSelect,
}: MeetingNoteCardProps) {
  const navigate = useNavigate();
  const { openTab } = useTabs();
  const { meetings, projects } = useStores();
  const storeMeeting = meetings.get(meetingId);
  const { data: calendarEvents = [] } = meetingApis.useCalendarEventsQuery();
  const [menuOpen, setMenuOpen] = useState(false);
  const [projectMenuOpen, setProjectMenuOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const handingOffToShare = useRef(false);

  if (!storeMeeting) return null;

  const meeting = storeMeetingToMeeting(storeMeeting);

  if (!meeting) return null;

  const event = meeting.event_id
    ? calendarEvents.find((e) => e.id === meeting.event_id)
    : null;
  const attendeesForDisplay = getDisplayAttendees(
    event?.attendees,
    meeting.attendees
  );

  const meetingTitle = getMeetingTitle(meeting);
  const isRecording = meetingStore.isMeetingIdRecording(meetingId);
  const isPrepGenerating = storeMeeting.prepStatus === 'in_progress';
  const hasNotification = meetings.get(meetingId)?.hasNotification ?? false;
  const currentProject = projects.getProjectForMeeting(meetingId);
  const noteSource = getMeetingNoteSource(storeMeeting.externalSource);

  const handleCardClick = (e?: React.MouseEvent) => {
    if (menuOpen || projectMenuOpen) return;
    if (selectionActive) {
      if (isRecording) return;
      onToggleSelect(meetingId);
      return;
    }
    trackEvent('MEETING_VIEWED', {
      meeting_id: meetingId,
      meeting_title: meetingTitle,
      has_calendar_event: Boolean(meeting.event_id),
    });
    const defaultTab = getDefaultMeetingTab(storeMeeting.externalSource);
    if (e && isNewTabClick(e)) {
      openTab({ path: `/meetings/${meetingId}`, search: { tab: defaultTab } });
    } else {
      navigate({
        to: '/meetings/$meetingId',
        params: { meetingId },
        search: { tab: defaultTab },
      });
    }
  };

  return (
    <div className="group relative">
      <div
        className={cn(
          'absolute inset-y-0 right-full w-6',
          'flex items-center justify-center',
          'transition-opacity duration-150',
          selectionActive && !isRecording
            ? 'opacity-100'
            : isRecording
              ? 'opacity-0 pointer-events-none'
              : 'opacity-0 group-hover:opacity-100 has-[:focus-visible]:opacity-100 group-has-[:focus-visible]:opacity-100'
        )}
        onClick={(e) => {
          e.stopPropagation();
          if (e.target === e.currentTarget) onToggleSelect(meetingId);
        }}
      >
        <Checkbox
          checked={isSelected}
          onCheckedChange={() => onToggleSelect(meetingId)}
          disabled={isRecording}
          aria-label={`Select ${meetingTitle}`}
        />
      </div>
      <XStack
        data-testid={`meeting-card-${meetingId}`}
        className={cn(
          'min-w-0 gap-4 items-center',
          'p-2 rounded-2xl',
          'cursor-default',
          'focus-ring-inset',
          'group-hover:bg-surface-hover transition-colors',
          (isSelected || menuOpen || projectMenuOpen) && 'bg-surface-hover'
        )}
        role="button"
        aria-label={meetingTitle}
        onClick={handleCardClick}
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') handleCardClick(undefined);
        }}
      >
        <MeetingItemLeftVisual attendees={attendeesForDisplay} />

        <YStack className="flex-1 min-w-0 gap-0.5 no-record">
          <XStack className="items-center gap-1.5 min-w-0">
            <Text
              className={cn(
                'font-medium text-base truncate',
                isPrepGenerating && MEETING_TITLE_SHIMMER_CLASSES
              )}
            >
              {meetingTitle}
            </Text>
            {noteSource && <MeetingNoteSourceBadge source={noteSource} />}
            {hasNotification && (
              <ActiveDot className="shrink-0 motion-opacity-in-0 motion-scale-in-0" />
            )}
          </XStack>
          <Text
            className={cn(
              'text-xs truncate',
              isRecording
                ? 'font-medium text-brand-cyan-cornflower-blue dark:text-brand-jordy-blue'
                : 'text-foreground-muted'
            )}
          >
            {isRecording ? (
              <span className="inline-flex items-center gap-1">
                <span className="size-[5px] shrink-0 rounded-full bg-brand-cyan-cornflower-blue dark:bg-brand-jordy-blue" />
                Live
              </span>
            ) : (
              formatAttendees(attendeesForDisplay)
            )}
          </Text>
        </YStack>

        <XStack className="shrink-0 items-center relative min-w-0 justify-end">
          {currentProject && (
            <MoveToProjectDropdownButton
              meetingId={meetingId}
              onOpenChange={setProjectMenuOpen}
              className="max-w-40 text-xs"
            />
          )}
          <Text
            className={cn(
              'text-xs text-foreground-muted px-2 w-20 text-right pointer-events-none',
              'transition-opacity duration-150',
              (menuOpen || projectMenuOpen || undefined) && 'opacity-0',
              'group-hover:opacity-0 group-has-[:focus-visible]:opacity-0'
            )}
          >
            <Time
              date={
                meeting.first_transcript_at ??
                event?.start_datetime ??
                meeting.created_at
              }
            />
          </Text>
          <XStack className="absolute right-2 top-1/2 -translate-y-1/2 items-center gap-0.5">
            {!currentProject && (
              <MoveToProjectDropdownButton
                meetingId={meetingId}
                onOpenChange={setProjectMenuOpen}
                className={cn(
                  'max-w-40 text-xs',
                  'opacity-0 pointer-events-none transition-opacity duration-150',
                  'touch:opacity-100 touch:pointer-events-auto',
                  'group-hover:opacity-100 focus-visible:opacity-100 group-has-[:focus-visible]:opacity-100 group-has-[:focus-visible]:pointer-events-auto group-hover:pointer-events-auto',
                  'data-[state=open]:opacity-100 data-[state=open]:pointer-events-auto'
                )}
              />
            )}
            <DropdownMenu.Root
              open={menuOpen}
              onOpenChange={(open) => {
                if (open) setShareOpen(false);
                setMenuOpen(open);
              }}
            >
              <ShareMeetingPopover
                meetingId={meetingId}
                open={shareOpen}
                onOpenChange={setShareOpen}
                anchorOnly
              >
                <DropdownMenu.Trigger asChild>
                  <IconButton
                    icon={Icons.moreHorizontal}
                    size="default"
                    variant="secondary"
                    aria-label="Meeting actions"
                    className={cn(
                      'opacity-0 transition-opacity duration-150',
                      'touch:opacity-100',
                      'data-[state=open]:opacity-100 data-[state=open]:bg-secondary-hover',
                      'group-hover:opacity-100 focus-visible:opacity-100 group-has-[:focus-visible]:opacity-100',
                      projectMenuOpen && 'opacity-100',
                      shareOpen && 'opacity-0 group-hover:opacity-0'
                    )}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                  />
                </DropdownMenu.Trigger>
              </ShareMeetingPopover>
              <DropdownMenu.Content
                align="end"
                className="min-w-36"
                fit
                onCloseAutoFocus={(e) => {
                  if (!handingOffToShare.current) return;
                  e.preventDefault();
                  handingOffToShare.current = false;
                }}
              >
                <MeetingNoteDropdownItems
                  meetingId={meetingId}
                  onShare={() => {
                    handingOffToShare.current = true;
                    setShareOpen(true);
                  }}
                />
              </DropdownMenu.Content>
            </DropdownMenu.Root>
          </XStack>
        </XStack>
      </XStack>
    </div>
  );
});

const CONFIRM_RESET_MS = 4000;

const MEETINGS_SCROLL_STORAGE_KEY = 'meetings-list-scroll';

type UpcomingEventItem = { event: CalendarEvent; meeting?: Meeting };

type RowItem =
  | { type: 'banners' }
  | {
      type: 'upcoming-section';
      page: number;
      onPrevPage: () => void;
      onNextPage: () => void;
      onGoToToday: () => void;
      isLoading: boolean;
      events: UpcomingEventItem[];
    }
  | { type: 'no-upcoming-events' }
  | { type: 'empty-meetings' }
  | { type: 'empty-search'; query: string }
  | { type: 'empty-project'; noProject: boolean }
  | {
      type: 'project-shared-meetings';
      projectId: string;
      hasOwnMeetings: boolean;
    }
  | { type: 'label-date'; date: string }
  | { type: 'meeting'; meetingId: MeetingId }
  | { type: 'loading-more-meetings' }
  | { type: 'bottom-spacer' };

function rowKey(row: RowItem): string {
  switch (row.type) {
    case 'label-date':
      return `label-date:${row.date}`;
    case 'meeting':
      return `meeting:${row.meetingId}`;
    default:
      return row.type;
  }
}

function Row({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return <div className={cn(Page.column(), className)}>{children}</div>;
}

const UPCOMING_PAGE_SIZE = 3;

function upcomingPageCount(eventCount: number): number {
  return Math.max(1, Math.ceil(eventCount / UPCOMING_PAGE_SIZE));
}

function isTodayEvent({ event }: UpcomingEventItem): boolean {
  return isToday(new Date(event.start_datetime));
}

function UpcomingSection({
  events,
  isLoading,
  page,
  onPrevPage,
  onNextPage,
  onGoToToday,
}: {
  events: UpcomingEventItem[];
  isLoading: boolean;
  page: number;
  onPrevPage: () => void;
  onNextPage: () => void;
  onGoToToday: () => void;
}) {
  const pageCount = upcomingPageCount(events.length);
  const pageStart = page * UPCOMING_PAGE_SIZE;
  const visible = events.slice(pageStart, pageStart + UPCOMING_PAGE_SIZE);
  const showTodayPill =
    events.some(isTodayEvent) && !visible.some(isTodayEvent);
  return (
    <YStack className="rounded-2xl border border-border-strong bg-surface shadow-sm overflow-hidden p-3">
      <XStack className="group/calendar-sync items-center justify-between px-2 pt-1 pb-3">
        <Text className="text-sm text-foreground-muted">Coming up</Text>
        <XStack className="items-center gap-1.5">
          <CalendarSyncControl showReconnect={false} />
          {showTodayPill && (
            <Button
              variant="ghost"
              size="sm"
              className="h-6 rounded-full border border-border-strong px-2.5 text-xs text-foreground-muted hover:text-foreground"
              onClick={onGoToToday}
            >
              Today
            </Button>
          )}
          {pageCount > 1 && (
            <XStack className="items-center gap-0.5">
              <Button
                variant="ghost"
                size="icon-sm"
                className="text-foreground-muted hover:text-foreground"
                disabled={page === 0}
                onClick={onPrevPage}
                aria-label="Previous events"
              >
                <Icons.chevronLeft size={14} />
              </Button>
              <Button
                variant="ghost"
                size="icon-sm"
                className="text-foreground-muted hover:text-foreground"
                disabled={page >= pageCount - 1}
                onClick={onNextPage}
                aria-label="Next events"
              >
                <Icons.chevronRight size={14} />
              </Button>
            </XStack>
          )}
        </XStack>
      </XStack>
      {isLoading ? (
        <YStack className="gap-0.5">
          <UpcomingEventCardSkeleton />
          <UpcomingEventCardSkeleton />
          <UpcomingEventCardSkeleton />
        </YStack>
      ) : (
        <YStack className="gap-0.5">
          {visible.map(({ event, meeting }) => (
            <UpcomingEventCard
              key={event.id}
              event={event}
              existingMeeting={meeting}
            />
          ))}
        </YStack>
      )}
    </YStack>
  );
}

function useMeetingsFiltering(
  query: string,
  filter: MeetingsFilter,
  circleMeetingIdList: string[] | null,
  excludeMeetingIds: ReadonlySet<string>
) {
  const { meetings } = useStores();
  const { data: calendarEvents = [] } = meetingApis.useCalendarEventsQuery();
  const circleMeetingIds = circleMeetingIdList
    ? new Set(circleMeetingIdList)
    : null;

  const meetingsForFilter = circleMeetingIds
    ? meetings.sortedMeetings.filter((meeting) =>
        circleMeetingIds.has(meeting.id)
      )
    : meetings.getMeetingsForFilter(filter);
  const meetingIds = meetingsForFilter.map((m) => m.id);
  const filteredIds = filterMeetingIdsBySearch(
    meetingIds,
    query,
    meetings,
    calendarEvents
  );
  const matchedIds = new Set<string>(filteredIds);
  const eventStartsById = new Map(
    calendarEvents.map((event) => [event.id, event.start_datetime])
  );
  const groupedMeetings = groupMeetingsByDate(
    meetingsForFilter.filter((meeting) => matchedIds.has(meeting.id)),
    { excludeMeetingIds, eventStartsById }
  );
  const hasQuery = query.trim().length > 0;
  return {
    meetings,
    meetingIds,
    filteredIds,
    groupedMeetings,
    hasQuery,
  };
}

function useUpcomingPanel(
  filter: MeetingsFilter,
  circle: MeetingCircle | null
) {
  const {
    upcomingMeetingsWithStatus: allUpcoming,
    isLoading: isLoadingUpcoming,
  } = useUpcomingMeetings();
  const upcomingMeetingsWithStatus = filterUpcomingMeetingsByProject(
    allUpcoming,
    filter
  ).filter(
    ({ event, meeting }) =>
      !circle ||
      isInCircle(circle, {
        eventId: event.id,
        seriesId: event.calendar_series_id,
        emails: circleMemberEmails(
          userStore.user?.email,
          meeting?.attendees,
          event.attendees
        ),
      })
  );
  const sliceKey = `${JSON.stringify(filter)}:${circle?.key ?? ''}`;
  const [pagedSliceKey, setPagedSliceKey] = useState(sliceKey);
  const [rawPage, setRawPage] = useState(0);
  if (pagedSliceKey !== sliceKey) {
    setPagedSliceKey(sliceKey);
    setRawPage(0);
  }

  const pageCount = upcomingPageCount(upcomingMeetingsWithStatus.length);
  const page = Math.min(rawPage, pageCount - 1);
  const goToPrevPage = useCallback(
    () => setRawPage(Math.max(0, page - 1)),
    [page]
  );
  const goToNextPage = useCallback(
    () => setRawPage(Math.min(pageCount - 1, page + 1)),
    [page, pageCount]
  );
  const goToToday = useCallback(() => {
    const todayIndex = upcomingMeetingsWithStatus.findIndex(isTodayEvent);
    setRawPage(
      todayIndex === -1 ? 0 : Math.floor(todayIndex / UPCOMING_PAGE_SIZE)
    );
  }, [upcomingMeetingsWithStatus]);
  return {
    upcomingMeetingsWithStatus,
    isLoadingUpcoming,
    page,
    goToPrevPage,
    goToNextPage,
    goToToday,
  };
}

interface BulkSelection {
  selectedIds: Set<MeetingId>;
  selectionActive: boolean;
  toggleSelect: (id: MeetingId) => void;
  clearSelection: () => void;
  isMerging: boolean;
  confirmingDelete: boolean;
  confirmingMerge: boolean;
  handleBulkDelete: () => void;
  handleBulkMerge: () => void;
}

function useBulkSelection(meetingIds: MeetingId[]): BulkSelection {
  const navigate = useNavigate();
  const [selectedIds, setSelectedIds] = useState<Set<MeetingId>>(new Set());
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [confirmingMerge, setConfirmingMerge] = useState(false);
  const [isMerging, setIsMerging] = useState(false);
  const confirmTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const mergeTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const selectionActive = selectedIds.size > 0;

  const toggleSelect = useCallback((id: MeetingId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
    setConfirmingDelete(false);
    setConfirmingMerge(false);
    clearTimeout(confirmTimerRef.current);
    clearTimeout(mergeTimerRef.current);
  }, []);

  const handleBulkDelete = useCallback(async () => {
    if (!confirmingDelete) {
      setConfirmingDelete(true);
      confirmTimerRef.current = setTimeout(
        () => setConfirmingDelete(false),
        CONFIRM_RESET_MS
      );
      return;
    }
    clearTimeout(confirmTimerRef.current);
    const ids = [...selectedIds];
    clearSelection();
    await meetingApis.deleteMeetingNotes(ids);
  }, [confirmingDelete, selectedIds, clearSelection]);

  const handleBulkMerge = useCallback(async () => {
    if (isMerging) return;
    if (!confirmingMerge) {
      setConfirmingMerge(true);
      mergeTimerRef.current = setTimeout(
        () => setConfirmingMerge(false),
        CONFIRM_RESET_MS
      );
      return;
    }
    clearTimeout(mergeTimerRef.current);
    const ids = [...selectedIds];
    setIsMerging(true);
    try {
      const newMeetingId = await meetingApis.mergeMeetingNotes(ids);
      clearSelection();
      if (newMeetingId) {
        navigate({
          to: '/meetings/$meetingId',
          params: { meetingId: newMeetingId },
          search: { tab: 'notes' },
        });
      }
    } finally {
      setIsMerging(false);
    }
  }, [isMerging, confirmingMerge, selectedIds, clearSelection, navigate]);

  useEffect(
    () => () => {
      clearTimeout(confirmTimerRef.current);
      clearTimeout(mergeTimerRef.current);
    },
    []
  );

  // Escape clears selection
  useEffect(() => {
    if (!selectionActive) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') clearSelection();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [selectionActive, clearSelection]);

  // Prune stale selected ids when meeting list changes
  useEffect(() => {
    if (!selectionActive) return;
    const meetingIdSet = new Set(meetingIds);
    setSelectedIds((prev) => {
      const next = new Set([...prev].filter((id) => meetingIdSet.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [meetingIds, selectionActive]);

  return {
    selectedIds,
    selectionActive,
    toggleSelect,
    clearSelection,
    isMerging,
    confirmingDelete,
    confirmingMerge,
    handleBulkDelete,
    handleBulkMerge,
  };
}

export interface MeetingRowsInput {
  query: string;
  hasQuery: boolean;
  filter: MeetingsFilter;
  isLoadingUpcoming: boolean;
  meetingIds: MeetingId[];
  filteredIds: MeetingId[];
  groupedMeetings: MeetingDateGroup<StoreMeeting>[];
  upcomingMeetingsWithStatus: { event: CalendarEvent; meeting?: Meeting }[];
  upcomingPage: number;
  onPrevUpcomingPage: () => void;
  onNextUpcomingPage: () => void;
  onGoToUpcomingToday: () => void;
  /** Whether a paginated request for additional meetings is in flight. */
  isLoadingMoreMeetings?: boolean;
  /** When the onboarding checklist is visible on the unsliced home and the user has no meetings, suppress the upcoming and empty rows so the checklist renders alone. */
  shouldShowOnboardingChecklist?: boolean;
  isSliced?: boolean;
  isLoadingSlice?: boolean;
}

/** Index of the `banners` row in the VList; kept mounted so CalendarStatusBanner does not refetch on scroll. */
export const BANNERS_ROW_INDEX = 0;

export function buildMeetingRows(input: MeetingRowsInput): RowItem[] {
  const {
    query,
    hasQuery,
    filter,
    isLoadingUpcoming,
    meetingIds,
    filteredIds,
    groupedMeetings,
    upcomingMeetingsWithStatus,
    upcomingPage,
    onPrevUpcomingPage,
    onNextUpcomingPage,
    onGoToUpcomingToday,
    isLoadingMoreMeetings = false,
    shouldShowOnboardingChecklist = false,
    isSliced = false,
    isLoadingSlice = false,
  } = input;

  const rows: RowItem[] = [];

  rows.push({ type: 'banners' });

  if (shouldShowOnboardingChecklist && !isSliced && meetingIds.length === 0) {
    rows.push({ type: 'bottom-spacer' });
    return rows;
  }

  const showUpcomingSection =
    upcomingMeetingsWithStatus.length > 0 ||
    (isLoadingUpcoming && meetingIds.length > 0);

  if (showUpcomingSection) {
    rows.push({
      type: 'upcoming-section',
      page: upcomingPage,
      onPrevPage: onPrevUpcomingPage,
      onNextPage: onNextUpcomingPage,
      onGoToToday: onGoToUpcomingToday,
      isLoading: isLoadingUpcoming,
      events: upcomingMeetingsWithStatus,
    });
  } else if (meetingIds.length > 0 && !isSliced) {
    rows.push({ type: 'no-upcoming-events' });
  }

  if (meetingIds.length === 0) {
    if (isLoadingSlice) {
      rows.push({ type: 'loading-more-meetings' });
    } else if (filter.kind === 'all-meetings') {
      if (!isSliced) rows.push({ type: 'empty-meetings' });
    } else if (filter.kind === 'project') {
      rows.push({
        type: 'project-shared-meetings',
        projectId: filter.projectId,
        hasOwnMeetings: false,
      });
    } else {
      rows.push({
        type: 'empty-project',
        noProject: filter.kind === 'no-project',
      });
    }
  } else if (hasQuery && filteredIds.length === 0) {
    rows.push({ type: 'empty-search', query });
  } else {
    // Meetings linked to a still-upcoming event are dropped during grouping —
    // they already render in the "Coming up" section above.
    groupedMeetings.forEach((group) => {
      rows.push({ type: 'label-date', date: group.date });
      group.meetings.forEach((meeting) =>
        rows.push({ type: 'meeting', meetingId: meeting.id })
      );
    });
    if (isLoadingMoreMeetings) {
      rows.push({ type: 'loading-more-meetings' });
    } else if (filter.kind === 'project' && !hasQuery) {
      rows.push({
        type: 'project-shared-meetings',
        projectId: filter.projectId,
        hasOwnMeetings: true,
      });
    }
  }

  rows.push({ type: 'bottom-spacer' });

  return rows;
}

function useMeetingListing(
  query: string,
  filter: MeetingsFilter,
  circle: MeetingCircle | null
) {
  const upcoming = useUpcomingPanel(filter, circle);
  const circleLoad = useCircleMeetingsLoad(circle, query.trim().length > 0);
  const filtering = useMeetingsFiltering(
    query,
    filter,
    circle ? (query.trim() ? circle.meetingIds : circleLoad.meetingIds) : null,
    collectUpcomingMeetingIds(upcoming.upcomingMeetingsWithStatus)
  );
  const selection = useBulkSelection(filtering.meetingIds);
  const pagination = useMeetingListPagination(filter);

  const { runLoadMore, isLoadingMore: isLoadingMoreMeetings } = usePagedLoad(
    circle
      ? {
          enabled: !filtering.hasQuery,
          canLoadMore: circleLoad.hasMore,
          loadPage: circleLoad.loadMore,
        }
      : {
          enabled: pagination.isLoaded && !filtering.hasQuery,
          canLoadMore: pagination.hasMore,
          loadPage: pagination.loadNextPage,
        }
  );

  const rows = buildMeetingRows({
    query,
    hasQuery: filtering.hasQuery,
    filter,
    isLoadingUpcoming: upcoming.isLoadingUpcoming,
    meetingIds: filtering.meetingIds,
    filteredIds: filtering.filteredIds,
    groupedMeetings: filtering.groupedMeetings,
    upcomingMeetingsWithStatus: upcoming.upcomingMeetingsWithStatus,
    upcomingPage: upcoming.page,
    onPrevUpcomingPage: upcoming.goToPrevPage,
    onNextUpcomingPage: upcoming.goToNextPage,
    onGoToUpcomingToday: upcoming.goToToday,
    isLoadingMoreMeetings,
    // The setup checklist (mic, "try it out") can't work in the browser since
    // recording is desktop-only — never show it on web.
    shouldShowOnboardingChecklist:
      !inBrowser() && meetingStore.shouldShowOnboardingChecklist,
    isSliced: meetingsSidebarStore.hasSlice,
    isLoadingSlice: circleLoad.isLoading,
  });

  return {
    rows,
    selection,
    runLoadMore,
    hasMeetings: filtering.meetingIds.length > 0,
    isLoadingMoreMeetings,
    isLoading: !pagination.isLoaded,
  };
}

const MeetingNotesSection = observer(function MeetingNotesSection({
  query,
}: {
  query: string;
}) {
  'use no memo';
  const { filter } = meetingsSidebarStore;
  const { meetings } = useStores();
  const {
    rows,
    selection,
    runLoadMore,
    hasMeetings,
    isLoadingMoreMeetings,
    isLoading,
  } = useMeetingListing(query, filter, useSelectedCircle());
  const {
    selectedIds,
    selectionActive,
    toggleSelect,
    clearSelection,
    isMerging,
    confirmingDelete,
    confirmingMerge,
    handleBulkDelete,
    handleBulkMerge,
  } = selection;

  const vlistRef = useRef<VirtualizerHandle>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const persistScroll = useVListScrollPersistence(
    vlistRef,
    MEETINGS_SCROLL_STORAGE_KEY,
    { ready: !isLoading }
  );

  const isNearEnd = useCallback(() => {
    if (rows.length === 0) return false;
    const list = vlistRef.current;
    if (!list) return false;
    return list.findEndIndex() >= rows.length - 5;
  }, [rows.length]);

  const { check: checkEnd } = useEndReachedLoadMore({
    isLoadingMore: isLoadingMoreMeetings,
    runLoadMore,
    isNearEnd,
  });

  const onScrollOffset = useScrollFadeForOffset(containerRef, {
    bottomFade: CHAT_DOCK_INSET,
  });

  const handleListScroll = useCallback(
    (offset: number) => {
      onScrollOffset(offset);
      persistScroll(offset);
      checkEnd();
    },
    [onScrollOffset, persistScroll, checkEnd]
  );

  // Render nothing until the filtered page resolves. The query is local
  // (local SQLite) so this is near-instant; showing nothing avoids a skeleton
  // flash on filter change (matches the All Chats list).
  if (isLoading) return null;

  return (
    <YStack
      ref={containerRef}
      data-testid="meetings-home-list"
      data-store-loaded={meetings.isLoaded}
      className="flex-1 min-h-0 relative"
    >
      <VList
        ref={vlistRef}
        onScroll={handleListScroll}
        overscan={10}
        keepMounted={[BANNERS_ROW_INDEX]}
        className="flex-1"
      >
        {rows.map((row) => {
          switch (row.type) {
            case 'banners':
              return (
                <Row
                  key={rowKey(row)}
                  className={cn('flex flex-col gap-6 pb-2', Page.topPadding)}
                >
                  <SliceTitle />
                  {inBrowser() ? (
                    // The empty state carries the download callout when there
                    // are no notes; only show it here once notes exist.
                    hasMeetings &&
                    !userStore.isOnboarded && (
                      <MeetingsDownloadCallout variant="banner" />
                    )
                  ) : (
                    <>
                      <MeetingLimitBanner />
                      <OnboardingChecklist />
                      <MicPermissionBanner />
                      <CalendarStatusBanner />
                      <AutomaticMeetingPrepBanner />
                    </>
                  )}
                </Row>
              );
            case 'upcoming-section':
              return (
                <Row key={rowKey(row)} className={cn('pt-2', 'pb-3')}>
                  <UpcomingSection
                    events={row.events}
                    isLoading={row.isLoading}
                    page={row.page}
                    onPrevPage={row.onPrevPage}
                    onNextPage={row.onNextPage}
                    onGoToToday={row.onGoToToday}
                  />
                </Row>
              );
            case 'no-upcoming-events':
              return (
                <Row key={rowKey(row)} className="pt-4 pb-3">
                  <MeetingsNoUpcomingEventsEmptyState />
                </Row>
              );
            case 'empty-meetings':
              return (
                <Row key={rowKey(row)} className="pt-4">
                  {inBrowser() && !userStore.isOnboarded ? (
                    <MeetingsDownloadCallout variant="empty" />
                  ) : (
                    <MeetingsEmptyState
                      showConnectCalendar={!userStore.calendarConnected}
                    />
                  )}
                </Row>
              );
            case 'empty-search':
              return (
                <Row key={rowKey(row)} className="pt-4">
                  <MeetingsSearchEmptyState query={row.query} />
                </Row>
              );
            case 'empty-project':
              return (
                <Row key={rowKey(row)} className="pt-4">
                  <MeetingsProjectEmptyState noProject={row.noProject} />
                </Row>
              );
            case 'project-shared-meetings':
              return (
                <Row
                  key={rowKey(row)}
                  className={row.hasOwnMeetings ? 'pt-7' : 'pt-4'}
                >
                  <ProjectSliceSharedMeetings
                    projectId={row.projectId}
                    hasOwnMeetings={row.hasOwnMeetings}
                  />
                </Row>
              );
            case 'label-date':
              return (
                <Row key={rowKey(row)} className="pt-7 pb-3">
                  <Text className="text-sm text-foreground-muted px-2">
                    {getMeetingGroupLabel(row.date)}
                  </Text>
                </Row>
              );
            case 'meeting':
              return (
                <Row key={rowKey(row)} className="pb-1">
                  <MeetingNoteCard
                    meetingId={row.meetingId}
                    isSelected={selectedIds.has(row.meetingId)}
                    selectionActive={selectionActive}
                    onToggleSelect={toggleSelect}
                  />
                </Row>
              );
            case 'loading-more-meetings':
              return (
                <Row key={rowKey(row)} className="py-2">
                  <Text className="text-center text-xs text-foreground-muted">
                    Loading more...
                  </Text>
                </Row>
              );
            case 'bottom-spacer':
              return (
                <div
                  key={rowKey(row)}
                  style={{ height: `var(${CHAT_DOCK_INSET_VAR}, 0px)` }}
                />
              );
          }
        })}
      </VList>

      <AnimatePresence>
        {selectionActive && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.15 }}
            className="absolute left-0 right-0 flex justify-center z-10 pointer-events-none"
            style={{
              bottom: `calc(var(${CHAT_DOCK_INSET_VAR}, 0px) + 0.75rem)`,
            }}
          >
            <XStack className="items-center gap-2 pointer-events-auto">
              <Button
                size="sm"
                variant="secondary"
                onClick={clearSelection}
                disabled={isMerging}
                className="gap-1.5"
              >
                {selectedIds.size} selected
                <Icons.cancel className="size-3" />
              </Button>
              {selectedIds.size >= 2 && (
                <Button
                  size="sm"
                  variant={confirmingMerge ? 'default' : 'secondary'}
                  onClick={handleBulkMerge}
                  isLoading={isMerging}
                  disabled={isMerging}
                  className="gap-1.5"
                >
                  <Icons.merge className="size-3.5" />
                  {confirmingMerge
                    ? `Merge ${selectedIds.size} meetings?`
                    : 'Merge'}
                </Button>
              )}
              <BulkMoveToProjectDropdown
                targets={[...selectedIds].map((id) => ({
                  kind: 'meeting',
                  id,
                }))}
                onComplete={clearSelection}
                disabled={isMerging}
              />
              <Button
                size="sm"
                variant={confirmingDelete ? 'destructive' : 'secondary'}
                onClick={handleBulkDelete}
                disabled={isMerging}
                className="gap-1.5"
              >
                <Icons.trash className="size-3.5" />
                {confirmingDelete ? 'Confirm?' : 'Delete'}
              </Button>
            </XStack>
          </motion.div>
        )}
      </AnimatePresence>
    </YStack>
  );
});

export function filterUpcomingMeetingsByProject(
  upcoming: UpcomingEventItem[],
  filter: MeetingsFilter
): UpcomingEventItem[] {
  if (filter.kind === 'all-meetings') return upcoming;

  return upcoming.filter(({ meeting }) =>
    filter.kind === 'no-project'
      ? !meeting?.project_id
      : meeting?.project_id === filter.projectId
  );
}
